import type { RouteObject } from 'react-router-dom';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import './App.css';
import { DASHBOARD, GENERAL, GLOBAL_MAPS, MAINTENANCE, REMOTE, LOGS, MANUALS, SUPPORT } from './utils/constants';
import { ConfigProvider} from 'antd';
import type { ComponentConfig } from './AppUtils';
import { COMPONENT_REGISTRY } from './AppUtils';
import AppContent from './view/auth/AppContent';
import ErrorPage from './view/auth/ErrorPage';
import NoPermissionPage from './NoPermissionPage';
import WrongRoutePage from './WrongRoutePage';
import { FullPageSkeleton } from './FullPageSkeleton';


// Lazy load all route components
const Dashboard = lazy(() => import('./view/dashboard/Dashboard'));
const CheckAdmin = lazy(() => import('./view/general/CheckAdmin'));
const DashGlobal = lazy(() => import('./view/general/DashGlobal'));
const GlobalDashboard3 = lazy(() => import('./view/general/GlobalDashboard3'));
const RemoteWrapper = lazy(() => import('./view/remote/RemoteWrapper'));
const ExecutiveDashboardView = lazy(() => import('./view/executive/ExecutiveDashboard'));
const ErrorLoginPage = lazy(() => import('./view/error/errorLogin'));
const FactoryViewWrapper = lazy(() => import('./view/factory/FactoryViewWrapper'));
const ExecutiveViewWrapper = lazy(() => import('./view/executive/ExecutiveViewWrapper'));
const FactoryView = lazy(() => import('./view/factory/FactoryView'));
const MaintenanceScreen = lazy(() => import('./view/maintenance/MaintenanceScreen'));
const MaintenaceWrapper = lazy(() => import('./view/maintenance/MaintenanceWrapper'));
const MapsGlobalWithNoProps = lazy(() => import('./view/maps/MapsGlobalNoProps'));
const GlobalReports = lazy(() => import('./view/reports/GlobalReports'));
const LokiLogging = lazy(() => import('./view/lokiLogging/LokiLogging'));
const Manuals = lazy(() => import('./view/manuals/Manuals'));
const LokiWrapper=lazy(() => import('./view/lokiLogging/LokiWrapper'));
const Support = lazy(() => import('./view/support/Support'));

const toLowerCasePath = (path: string) => path.toLowerCase();

const createProtectedRoute = (componentKey: string, config: ComponentConfig) => {
  const Component = config.component;
  const Wrapper = config.wrapper;

  const element = (
    <Suspense fallback={<FullPageSkeleton />}>
      <Wrapper route={componentKey}>
        <Component />
      </Wrapper>
    </Suspense>
  );

  const route: RouteObject = {
    path: toLowerCasePath(componentKey),
    element,
    children: []
  };

  if (config.hasIndividual && config.individualComponent) {
    const IndividualComponent = config.individualComponent;
    route.children = [{
      path: `:${config.paramName}`,
      element: (
        <Suspense fallback={<FullPageSkeleton />}>
          <Wrapper route={componentKey}>
            <IndividualComponent />
          </Wrapper>
        </Suspense>
      )
    }];
  }

  return route;
};

const protectedRoutes = Object.keys(COMPONENT_REGISTRY).map(componentKey =>
  createProtectedRoute(componentKey, COMPONENT_REGISTRY[componentKey])
);

const router = createBrowserRouter([
  {
    path: '/403',
    element: <NoPermissionPage />,
    errorElement: <ErrorPage />
  },
  {
    path: '/404',
    element: <WrongRoutePage />,
    errorElement: <ErrorPage />
  },
  {
    path: "/",
    element: <AppContent />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: ":customer",
        element: (
          <Suspense fallback={<FullPageSkeleton />}>
            <GlobalDashboard3 />
          </Suspense>
        ),
        children: [
          {
            index: true,
            element: <Navigate to='dashboard' replace />
          },
          {
            path: DASHBOARD,
            element: (
              <Suspense fallback={<FullPageSkeleton />}>
                <DashGlobal />
              </Suspense>
            )
          },
          {
            path: MANUALS,
            element: (
              <Suspense fallback={<FullPageSkeleton />}>
                <Manuals />
              </Suspense>
            )
          },
          {
            path: SUPPORT,
            element: (
              <Suspense fallback={<FullPageSkeleton />}>
                <Support />
              </Suspense>
            )
          },
          {
            path: REMOTE,
            element: (
              <Suspense fallback={<FullPageSkeleton />}>
                <RemoteWrapper />
              </Suspense>
            )
          },
          {
            path: LOGS,
            element: (
              <Suspense fallback={<FullPageSkeleton />}>
                <LokiWrapper>
                <LokiLogging />
                </LokiWrapper>
              </Suspense>
            ),
            errorElement: <ErrorPage />
          },
          {
            path: GLOBAL_MAPS,
            element: (
              <Suspense fallback={<FullPageSkeleton />}>
                <MapsGlobalWithNoProps />
              </Suspense>
            )
          },
          {
            path: toLowerCasePath(MAINTENANCE),
            element: (
              <Suspense fallback={<FullPageSkeleton />}>
                <MaintenaceWrapper>
                  <MaintenanceScreen />
                </MaintenaceWrapper>
              </Suspense>
            )
          },
          {
            path: 'view/FactoryDashboard',
            element: (
              <Suspense fallback={<FullPageSkeleton />}>
                <FactoryViewWrapper>
                  <FactoryView />
                </FactoryViewWrapper>
              </Suspense>
            )
          },
          {
            path: "view/ExecutiveDashboard",
            element: (
              <Suspense fallback={<FullPageSkeleton />}>
                <ExecutiveViewWrapper>
                  <ExecutiveDashboardView />
                </ExecutiveViewWrapper>
              </Suspense>
            )
          },
          {
            path: 'view/Reports',
            element: (
              <Suspense fallback={<FullPageSkeleton />}>
                <GlobalReports />
              </Suspense>
            )
          },
          {
            path: ":site",
            element: (
              <Suspense fallback={<FullPageSkeleton />}>
                <Dashboard />
              </Suspense>
            ),
            children: [
              {
                index: true,
                element: <Navigate to={toLowerCasePath(GENERAL)} replace />
              },
              {
                path: toLowerCasePath(GENERAL),
                element: (
                  <Suspense fallback={<FullPageSkeleton />}>
                    <CheckAdmin />
                  </Suspense>
                )
              },
              {
                path: MANUALS,
                element: (
                  <Suspense fallback={<FullPageSkeleton />}>
                    <Manuals />
                  </Suspense>
                )
              },
              {
                path: SUPPORT,
                element: (
                  <Suspense fallback={<FullPageSkeleton />}>
                    <Support />
                  </Suspense>
                )
              },
              {
                path: toLowerCasePath(MAINTENANCE),
                element: (
                  <Suspense fallback={<FullPageSkeleton />}>
                    <MaintenaceWrapper>
                      <MaintenanceScreen />
                    </MaintenaceWrapper>
                  </Suspense>
                )
              },
              ...protectedRoutes,
              {
                path: "errorLogin",
                element: (
                  <Suspense fallback={<FullPageSkeleton />}>
                    <ErrorLoginPage />
                  </Suspense>
                )
              },
            ],
          },
        ]
      }
    ],
  },
]);

function App() {
  return (
    <ConfigProvider
      theme={{
        token: {
          fontFamily: "SF Pro Display",
        }
      }}
    >
      <RouterProvider
        router={router}
      />
    </ConfigProvider>
  );
}

export default App;